import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Package, Users, DollarSign, ShoppingCart, Loader2, CalendarIcon, Download } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { exportToCsv } from "@/lib/csv-export";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  AreaChart, Area,
} from "recharts";
import { format, subDays, startOfDay, differenceInDays } from "date-fns";

interface Stats {
  totalProducts: number;
  totalOrders: number;
  totalRevenue: number;
  totalCustomers: number;
}

interface Order {
  id: string;
  total_amount: number;
  shipping_fee: number;
  status: string;
  created_at: string;
}

interface OrderItem {
  product_name: string;
  quantity: number;
  unit_price: number;
}

const STATUS_COLORS: Record<string, string> = {
  pending: "hsl(45, 90%, 50%)",
  confirmed: "hsl(210, 80%, 55%)",
  processing: "hsl(270, 60%, 55%)",
  shipped: "hsl(230, 70%, 55%)",
  delivered: "hsl(140, 70%, 45%)",
  cancelled: "hsl(0, 70%, 50%)",
};

type RangePreset = "7d" | "30d" | "90d" | "custom";

const AdminStats = () => {
  const [stats, setStats] = useState<Stats>({
    totalProducts: 0,
    totalOrders: 0,
    totalRevenue: 0,
    totalCustomers: 0,
  });
  const [orders, setOrders] = useState<Order[]>([]);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [rangePreset, setRangePreset] = useState<RangePreset>("30d");
  const [customFrom, setCustomFrom] = useState<Date | undefined>(subDays(new Date(), 30));
  const [customTo, setCustomTo] = useState<Date | undefined>(new Date());

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [productsRes, ordersRes, customersRes, itemsRes] = await Promise.all([
        supabase.from("products").select("id", { count: "exact", head: true }),
        supabase.from("orders").select("id, total_amount, shipping_fee, status, created_at"),
        supabase.from("profiles").select("id", { count: "exact", head: true }),
        supabase.from("order_items").select("product_name, quantity, unit_price"),
      ]);

      const ordersData = ordersRes.data || [];
      const totalRevenue = ordersData.reduce(
        (sum, order) => sum + Number(order.total_amount || 0) - Number(order.shipping_fee || 0),
        0
      );

      setStats({
        totalProducts: productsRes.count || 0,
        totalOrders: ordersData.length,
        totalRevenue,
        totalCustomers: customersRes.count || 0,
      });
      setOrders(ordersData);
      setOrderItems(itemsRes.data || []);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  // Date range logic
  const getDateRange = (): { from: Date; to: Date; days: number } => {
    const to = startOfDay(new Date());
    if (rangePreset === "7d") return { from: subDays(to, 6), to, days: 7 };
    if (rangePreset === "90d") return { from: subDays(to, 89), to, days: 90 };
    if (rangePreset === "custom" && customFrom && customTo) {
      const days = differenceInDays(customTo, customFrom) + 1;
      return { from: startOfDay(customFrom), to: startOfDay(customTo), days };
    }
    return { from: subDays(to, 29), to, days: 30 };
  };

  const { from: rangeFrom, to: rangeTo, days: rangeDays } = getDateRange();

  const rangeLabel =
    rangePreset === "7d" ? "Last 7 Days" :
    rangePreset === "30d" ? "Last 30 Days" :
    rangePreset === "90d" ? "Last 90 Days" :
    customFrom && customTo ? `${format(customFrom, "MMM d")} – ${format(customTo, "MMM d")}` : "Custom";

  // Revenue over selected range
  const revenueData = (() => {
    const data: { date: string; revenue: number }[] = [];
    for (let i = rangeDays - 1; i >= 0; i--) {
      const day = startOfDay(subDays(rangeTo, i));
      const dayStr = format(day, "yyyy-MM-dd");
      const dayRevenue = orders
        .filter(o => format(new Date(o.created_at), "yyyy-MM-dd") === dayStr)
        .reduce((sum, o) => sum + Number(o.total_amount || 0) - Number(o.shipping_fee || 0), 0);
      data.push({ date: format(day, rangeDays > 31 ? "MMM d" : "MMM d"), revenue: dayRevenue });
    }
    return data;
  })();

  // Orders by status
  const statusData = (() => {
    const counts: Record<string, number> = {};
    orders.forEach(o => {
      counts[o.status] = (counts[o.status] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value,
      color: STATUS_COLORS[name] || "hsl(0, 0%, 50%)",
    }));
  })();

  // Top selling products
  const topProducts = (() => {
    const productMap: Record<string, { revenue: number; quantity: number }> = {};
    orderItems.forEach(item => {
      if (!productMap[item.product_name]) {
        productMap[item.product_name] = { revenue: 0, quantity: 0 };
      }
      productMap[item.product_name].revenue += item.unit_price * item.quantity;
      productMap[item.product_name].quantity += item.quantity;
    });
    return Object.entries(productMap)
      .map(([name, data]) => ({ name: name.length > 15 ? name.slice(0, 15) + '…' : name, revenue: data.revenue, quantity: data.quantity }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 8);
  })();

  const exportRevenueData = () => {
    exportToCsv(
      `revenue-${rangePreset}-${format(new Date(), "yyyy-MM-dd")}`,
      ["Date", "Revenue (EGP)"],
      revenueData.map((d) => [d.date, d.revenue])
    );
  };

  const exportOrdersData = () => {
    exportToCsv(
      `orders-summary-${format(new Date(), "yyyy-MM-dd")}`,
      ["Order ID", "Amount (EGP)", "Status", "Date"],
      orders.map((o) => [o.id.slice(0, 8), o.total_amount, o.status, format(new Date(o.created_at), "yyyy-MM-dd")])
    );
  };

  const exportTopProducts = () => {
    exportToCsv(
      `top-products-${format(new Date(), "yyyy-MM-dd")}`,
      ["Product", "Revenue (EGP)", "Quantity Sold"],
      topProducts.map((p) => [p.name, p.revenue, p.quantity])
    );
  };

  const statItems = [
    { label: "Total Products", value: stats.totalProducts.toString(), icon: Package, color: "text-blue-500", bg: "bg-blue-500/10" },
    { label: "Total Orders", value: stats.totalOrders.toString(), icon: ShoppingCart, color: "text-green-500", bg: "bg-green-500/10" },
    { label: "Revenue", value: `${stats.totalRevenue.toLocaleString()} EGP`, icon: DollarSign, color: "text-primary", bg: "bg-primary/10" },
    { label: "Customers", value: stats.totalCustomers.toString(), icon: Users, color: "text-purple-500", bg: "bg-purple-500/10" },
  ];

  const CustomTooltipStyle = {
    backgroundColor: "hsl(var(--card))",
    border: "1px solid hsl(var(--border))",
    borderRadius: "4px",
    color: "hsl(var(--foreground))",
    fontSize: "12px",
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-serif mb-8">Dashboard Overview</h1>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statItems.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="p-6 bg-card border border-border rounded-sm"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-muted-foreground text-sm">{stat.label}</p>
                <p className="text-3xl font-serif mt-2">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 rounded-full ${stat.bg} flex items-center justify-center`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts Row 1: Revenue + Orders by Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="font-serif text-lg">Revenue ({rangeLabel})</CardTitle>
            <div className="flex items-center gap-1 flex-wrap">
              {(["7d", "30d", "90d"] as const).map((preset) => (
                <Button
                  key={preset}
                  variant={rangePreset === preset ? "default" : "outline"}
                  size="sm"
                  className="h-7 text-xs px-2.5"
                  onClick={() => setRangePreset(preset)}
                >
                  {preset === "7d" ? "7D" : preset === "30d" ? "30D" : "90D"}
                </Button>
              ))}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={rangePreset === "custom" ? "default" : "outline"}
                    size="sm"
                    className="h-7 text-xs px-2.5"
                  >
                    <CalendarIcon className="w-3 h-3 mr-1" />
                    Custom
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 flex flex-col sm:flex-row" align="end">
                  <div className="p-3">
                    <p className="text-xs text-muted-foreground mb-2 font-medium">From</p>
                    <Calendar
                      mode="single"
                      selected={customFrom}
                      onSelect={(d) => { setCustomFrom(d); setRangePreset("custom"); }}
                      disabled={(date) => date > new Date()}
                      className={cn("p-0 pointer-events-auto")}
                    />
                  </div>
                  <div className="p-3 border-t sm:border-t-0 sm:border-l border-border">
                    <p className="text-xs text-muted-foreground mb-2 font-medium">To</p>
                    <Calendar
                      mode="single"
                      selected={customTo}
                      onSelect={(d) => { setCustomTo(d); setRangePreset("custom"); }}
                      disabled={(date) => date > new Date() || (customFrom ? date < customFrom : false)}
                      className={cn("p-0 pointer-events-auto")}
                    />
                  </div>
                </PopoverContent>
              </Popover>
              <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={exportRevenueData}>
                <Download className="w-3 h-3 mr-1" /> CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: "hsl(var(--border))" }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: "hsl(var(--border))" }}
                    tickFormatter={(v) => `${v}`}
                  />
                  <Tooltip
                    contentStyle={CustomTooltipStyle}
                    formatter={(value: number) => [`${value} EGP`, "Revenue"]}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    fill="url(#revenueGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="font-serif text-lg">Orders by Status</CardTitle>
            <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={exportOrdersData}>
              <Download className="w-3 h-3 mr-1" /> CSV
            </Button>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {statusData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="45%"
                      innerRadius={50}
                      outerRadius={85}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={CustomTooltipStyle} />
                    <Legend
                      wrapperStyle={{ fontSize: "12px", color: "hsl(var(--muted-foreground))" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                  No orders yet
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2: Top Selling Products */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="font-serif text-lg">Top Selling Products</CardTitle>
          <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={exportTopProducts}>
            <Download className="w-3 h-3 mr-1" /> CSV
          </Button>
        </CardHeader>
        <CardContent>
          <div className="h-[350px]">
            {topProducts.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topProducts} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: "hsl(var(--border))" }}
                    tickFormatter={(v) => `${v}`}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: "hsl(var(--border))" }}
                    width={120}
                  />
                  <Tooltip
                    contentStyle={CustomTooltipStyle}
                    formatter={(value: number, name: string) => [
                      name === "revenue" ? `${value} EGP` : value,
                      name === "revenue" ? "Revenue" : "Qty Sold",
                    ]}
                  />
                  <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} barSize={20} name="revenue" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                No sales data yet
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminStats;